<template>
  <div class="gsl-hrm-background-container">
    <div class="gsl-hrm-card-container">
      <div class="gsl-hrm-header-container">
        <oxd-text tag="h6" class="gsl-hrm-main-title">
          {{ $t('general.biometric_configuration') }}
        </oxd-text>
        <oxd-switch-input
          v-model="configuration.enable"
          label-position="left"
          :option-label="$t('general.enable')"
        />
      </div>
      <oxd-divider />
      <oxd-form ref="formRef" :loading="isLoading">
        <oxd-text tag="p" class="gsl-hrm-subtitle">
          {{ $t('admin.server_settings') }}
        </oxd-text>
        <oxd-form-row>
          <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.host"
                :label="$t('admin.host')"
                :rules="rules.host"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item class="gsl-hrm-column-half">
              <oxd-input-field
                v-model="configuration.port"
                :label="$t('admin.port')"
                :rules="rules.port"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item class="--offset-row-2">
              <oxd-input-field
                v-model="configuration.dbname"
                label="Database Name"
                :rules="rules.dbname"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
          <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.username"
                label="Username"
                :rules="rules.username"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.password"
                type="password"
                :label="$t('general.password')"
                :rules="rules.password"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
          <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.tableName"
                label="Table Name"
                :rules="rules.tableName"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.idColumn"
                label="Biometric User ID Column Name"
                :rules="rules.idColumn"
                required
              />
            </oxd-grid-item>
            <oxd-grid-item>
              <oxd-input-field
                v-model="configuration.timeColumn"
                label="Time Column Name"
                :rules="rules.timeColumn"
                required
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-divider class="gsl-hrm-form-divider" />

        <oxd-text tag="p" class="gsl-hrm-subtitle">
          {{ $t('admin.additional_settings') }}
        </oxd-text>
        <oxd-grid :cols="3" class="gsl-hrm-full-width-grid">
          <oxd-grid-item>
            <timezone-dropdown
              v-model="configuration.timezone"
              :rules="rules.timezone"
              required
            />
          </oxd-grid-item>
        </oxd-grid>

        <oxd-divider />

        <oxd-form-actions>
          <oxd-button
            type="button"
            class="gsl-hrm-left-space"
            display-type="secondary"
            :label="$t('general.save')"
            @click="onClickSave"
          />
        </oxd-form-actions>
      </oxd-form>
    </div>
  </div>
</template>
<script>
import {
  required,
  validPortRange,
  validHostnameFormat,
  shouldNotExceedCharLength,
} from '@/core/util/validation/rules';
import {reloadPage} from '@/core/util/helper/navigation';
import useForm from '@/core/util/composable/useForm';
import {APIService} from '@ohrm/core/util/services/api.service';
import {OxdSwitchInput} from '@ohrm/oxd';
import TimezoneDropdown from '@/orangehrmAttendancePlugin/components/TimezoneDropdown.vue';

const configurationModel = {
  enable: false,
  host: 'localhost',
  port: '3306',
  dbname: null,
  username: null,
  password: null,
  tableName: null,
  idColumn: null,
  timeColumn: null,
  timezone: null,
};
export default {
  components: {
    'oxd-switch-input': OxdSwitchInput,
    'timezone-dropdown': TimezoneDropdown,
  },
  props: {
    showSync: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/admin/biometric-config',
    );
    const {formRef, invalid, validate} = useForm();

    return {
      http,
      formRef,
      invalid,
      validate,
    };
  },
  data() {
    return {
      isLoading: false,
      configuration: {
        ...configurationModel,
      },
      rules: {
        host: [required, validHostnameFormat, shouldNotExceedCharLength(255)],
        port: [required, validPortRange(5, 0, 65535)],
        dbname: [required],
        username: [required, shouldNotExceedCharLength(255)],
        password: [required, shouldNotExceedCharLength(255)],
        tableName: [required, shouldNotExceedCharLength(100)],
        idColumn: [required, shouldNotExceedCharLength(100)],
        timeColumn: [required, shouldNotExceedCharLength(100)],
        timezone: [required],
      },
    };
  },
  computed: {},
  beforeMount() {
    this.isLoading = true;
    // Fetch biometric configuration
    this.http
      .getAll()
      .then((response) => {
        const {data} = response.data;

        this.configuration.enable = data.enable;
        this.configuration.host = data.host;
        this.configuration.port = data.port?.toString();
        this.configuration.dbname = data.dbname;
        this.configuration.username = data.username;
        this.configuration.password = data.password;
        this.configuration.tableName = data.tableName;
        this.configuration.idColumn = data.idColumn;
        this.configuration.timeColumn = data.timeColumn;
        this.getTimezone(data.timezone).then((timezone) => {
          this.configuration.timezone = timezone;
        });
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    async getTimezone(name) {
      try {
        const response = await this.http.request({
          method: 'GET',
          url: '/api/v2/attendance/timezones',
        });

        const {data} = response.data;
        const matchedTimezone = data.find((timezone) => timezone.name === name);

        return matchedTimezone
          ? {
              id: matchedTimezone.name,
              label: `(GMT${matchedTimezone.label}) ${matchedTimezone.name}`,
              _offset: parseFloat(matchedTimezone.offset),
            }
          : null;
      } catch (error) {
        return null;
      }
    },

    getRequestBody() {
      return {
        enable: this.configuration.enable,
        host: this.configuration.host,
        port: parseInt(this.configuration.port),
        dbname: this.configuration.dbname,
        username: this.configuration.username,
        password: this.configuration.password,
        tableName: this.configuration.tableName,
        idColumn: this.configuration.idColumn,
        timeColumn: this.configuration.timeColumn,
        timezone: this.configuration.timezone?.id,
      };
    },
    onClickSave() {
      this.validate().then(() => {
        if (this.invalid === true) return;
        this.isLoading = true;
        this.http
          .request({
            method: 'PUT',
            data: this.getRequestBody(),
          })
          .then(() => {
            return this.$toast.updateSuccess();
          })
          .finally(() => reloadPage());
      });
    },
  },
};
</script>
<style src="./biometric-configuration.scss" lang="scss" scoped></style>
